import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  input,
  linkedSignal,
  output,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { PefData } from '@sportello-balneari/pef-editor';
import { CardComponent } from '@sportello-balneari/shared-components';
import { ButtonModule } from 'primeng/button';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { TooltipModule } from 'primeng/tooltip';
import {
  isWidgetStructureObject,
  WidgetStructureObject,
} from '../../../api/interfaces';
import { PefStateService } from '../../../services/compiling/state/pef-state.service';
import { TenderContextService } from '../../../services/compiling/state/tender-context.service';

/**
 * PEF row holding the yearly canone. The flat value key is this prefix plus
 * `.<year>`, one column per year of the concession period.
 */
export const CANONE_PEF_BASE_KEY =
  'dettaglioCostiGodimento.concessioniSpaziTempiPubblicitari2';

function extractWidgets(
  form: Record<string, unknown>,
): { id: string; widget: WidgetStructureObject }[] {
  return Object.entries(form)
    .filter(([, value]) => isWidgetStructureObject(value))
    .map(([key, value]) => ({
      id: key,
      widget: value as WidgetStructureObject,
    }));
}

function fmtCurrency(v: number | null | undefined): string {
  if (v == null) return '—';
  return v.toLocaleString('it-IT', {
    style: 'currency',
    currency: 'EUR',
    maximumFractionDigits: 0,
  });
}

@Component({
  selector: 'lib-envelope-c-economic-offer',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    ButtonModule,
    CardComponent,
    FormsModule,
    InputNumberModule,
    InputTextModule,
    TooltipModule,
  ],
  template: `
    <div class="flex flex-col gap-4">
      <!-- Card 1: Canone annuo -->
      <lib-card>
        <ng-container title>Canone annuo</ng-container>
        <ng-container content>
          <div class="flex flex-col gap-4 pt-2">
            <div class="grid grid-cols-2 gap-3">
              <!-- Base d'asta -->
              <div
                class="flex flex-col gap-1 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3"
              >
                <span
                  class="text-xs font-semibold uppercase tracking-wide text-slate-400"
                  >Base d'asta</span
                >
                <span class="text-xl font-bold text-slate-800">{{
                  minCanoneFormatted()
                }}</span>
              </div>

              <!-- Tua offerta -->
              <div
                class="flex flex-col gap-1 rounded-xl border px-4 py-3"
                [class]="proposedCanoneCardClass()"
              >
                <label
                  class="text-xs font-semibold uppercase tracking-wide text-slate-400"
                  for="proposed-canone"
                  >Tua offerta</label
                >
                <p-inputnumber
                  [autofocus]="false"
                  inputId="proposed-canone"
                  [ngModel]="draftCanone()"
                  (ngModelChange)="draftCanone.set($event)"
                  (keyup.enter)="applyCanone()"
                  [readonly]="readonly()"
                  [disabled]="canoneDisabled()"
                  [min]="0"
                  [maxFractionDigits]="0"
                  mode="currency"
                  currency="EUR"
                  locale="it-IT"
                  placeholder="Inserisci l'offerta"
                  styleClass="w-full"
                  inputStyleClass="canone-input"
                />
              </div>
            </div>

            <!-- Apply the offer to every year of the period -->
            @if (canoneChanged() && !readonly()) {
              <div
                class="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-slate-50 px-3 py-2"
              >
                <span class="text-sm text-slate-600">
                  Il canone sarà applicato a tutti gli anni del periodo{{
                    periodLabel()
                  }}
                  e il PEF verrà ricalcolato.
                </span>
                <div class="flex items-center gap-2">
                  <p-button
                    label="Annulla"
                    size="small"
                    severity="secondary"
                    [text]="true"
                    [disabled]="canoneDisabled()"
                    (onClick)="resetCanone()"
                  />
                  <p-button
                    label="Applica"
                    size="small"
                    icon="pi pi-check"
                    [disabled]="canoneDisabled() || draftCanone() === null"
                    (onClick)="applyCanone()"
                  />
                </div>
              </div>
            } @else if (proposedCanone() === null) {
              <div
                class="flex items-center gap-2 rounded-lg bg-slate-50 px-3 py-2 text-sm text-slate-500"
              >
                <i class="pi pi-info-circle text-slate-400"></i>
                <span
                  >Inserisci il canone offerto: verrà riportato su tutti gli
                  anni del periodo nel PEF.</span
                >
              </div>
            }

            @if (recalculating()) {
              <div
                class="flex items-center gap-2 rounded-lg bg-blue-50 px-3 py-2 text-sm font-medium text-blue-700"
              >
                <i class="pi pi-spin pi-spinner"></i>
                <span>Ricalcolo del PEF in corso…</span>
              </div>
            }

            <!-- Comparison badge -->
            @if (
              minCanone() !== null &&
              minCanone() !== undefined &&
              proposedCanone() !== null &&
              proposedCanone() !== undefined
            ) {
              @if (proposedCanone()! >= minCanone()!) {
                <div
                  class="flex items-center gap-2 rounded-lg bg-green-50 px-3 py-2 text-sm font-medium text-green-700"
                >
                  <i class="pi pi-check-circle"></i>
                  <span
                    >Offerta superiore alla base d'asta di
                    {{ diffFormatted() }}</span
                  >
                </div>
              } @else {
                <div
                  class="flex items-center gap-2 rounded-lg bg-red-50 px-3 py-2 text-sm font-medium text-red-700"
                >
                  <i class="pi pi-exclamation-triangle"></i>
                  <span>Offerta inferiore al canone minimo</span>
                </div>
              }
            }
          </div>
        </ng-container>
      </lib-card>

      <!-- Card 2: Punteggio offerta economica -->
      <lib-card>
        <ng-container title>Punteggio offerta economica</ng-container>
        <ng-container content>
          @if (criteria() === null || criteria() === undefined) {
            <div
              class="mt-2 flex items-center gap-3 rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500"
            >
              <i class="pi pi-info-circle text-slate-400"></i>
              <span
                >Questo bando non prevede punteggio per l'offerta
                economica.</span
              >
            </div>
          } @else {
            <div class="flex flex-col gap-4 pt-2">
              <!-- Max score badge -->
              <div class="flex items-center gap-2">
                <span
                  class="rounded-full bg-blue-100 px-3 py-1 text-sm font-bold text-blue-700"
                >
                  Punteggio massimo: {{ criteria()!.maxScore }} pt
                </span>
              </div>

              <!-- Criteria description -->
              @if (criteria()!.criteriaDescription) {
                <p class="text-sm leading-relaxed text-slate-600">
                  {{ criteria()!.criteriaDescription }}
                </p>
              }

              <!-- Dynamic form fields -->
              @if (widgets().length > 0) {
                <div class="flex flex-col gap-4">
                  @for (item of widgets(); track item.id) {
                    <div class="flex flex-col gap-1">
                      <label
                        class="text-sm font-medium text-slate-700"
                        [for]="'eco-' + item.id"
                      >
                        {{
                          item.widget.customization['description'] ?? item.id
                        }}
                      </label>
                      @if (item.widget._mappedType === 'NumericUpDown') {
                        <p-inputnumber
                          [autofocus]="false"
                          [inputId]="'eco-' + item.id"
                          [ngModel]="getAnswer(item.id)"
                          (ngModelChange)="setAnswer(item.id, $event)"
                          [readonly]="readonly()"
                          [useGrouping]="true"
                          locale="it-IT"
                          styleClass="w-full"
                        />
                      } @else {
                        <input
                          pInputText
                          [id]="'eco-' + item.id"
                          [ngModel]="getAnswer(item.id)"
                          (ngModelChange)="setAnswer(item.id, $event)"
                          [readonly]="readonly()"
                          class="w-full"
                        />
                      }
                    </div>
                  }
                </div>
              }

              <!-- Calcola punteggio button (stub) -->
              <div class="flex justify-end pt-2">
                <button
                  pButton
                  type="button"
                  label="Calcola punteggio"
                  icon="pi pi-calculator"
                  [disabled]="true"
                  pTooltip="Funzionalità in arrivo"
                  tooltipPosition="left"
                ></button>
              </div>
            </div>
          }
        </ng-container>
      </lib-card>
    </div>
  `,
  styles: [
    `
      :host ::ng-deep .canone-input {
        width: 100%;
        padding: 0;
        border: none;
        background: transparent;
        box-shadow: none;
        font-size: 1.25rem;
        font-weight: 700;
        color: rgb(30 41 59);
      }
    `,
  ],
})
export class EnvelopeCEconomicOfferComponent {
  readonly pefData = input<PefData>({});
  readonly pefStartYear = input<number | null>(null);
  readonly pefEndYear = input<number | null>(null);
  /** True while the PEF is being recalculated on the server. */
  readonly recalculating = input<boolean>(false);
  /** Figures only: the offer and the economic answers cannot be changed. */
  readonly readonly = input<boolean>(false);

  /**
   * Emitted when the user confirms a new canone. The host writes it on every
   * year of the period and triggers the recalculation.
   */
  readonly canoneApply = output<number>();

  private readonly pefState = inject(PefStateService);
  private readonly tenderContext = inject(TenderContextService);

  protected readonly criteria = computed(() =>
    this.pefState.economicOfferCriteria(),
  );

  protected readonly minCanone = computed(
    () => this.tenderContext.tenderLotDetails()?.annualBaseFee ?? null,
  );

  protected readonly proposedCanone = computed<number | null>(() => {
    const year = this.pefStartYear();
    if (year == null) return null;
    const key = `${CANONE_PEF_BASE_KEY}.${year}`;
    const val = this.pefData()[key];
    if (typeof val !== 'number' || val === 0) return null;
    return val;
  });

  /** Editable copy, reset whenever the PEF reports a new canone. */
  protected readonly draftCanone = linkedSignal<number | null>(() =>
    this.proposedCanone(),
  );

  protected readonly canoneChanged = computed(
    () => this.draftCanone() !== this.proposedCanone(),
  );

  protected readonly canoneDisabled = computed(
    () => this.recalculating() || this.pefStartYear() == null,
  );

  protected readonly periodLabel = computed(() => {
    const start = this.pefStartYear();
    const end = this.pefEndYear();
    if (start == null || end == null) return '';
    return ` (${start}–${end})`;
  });

  protected readonly minCanoneFormatted = computed(() =>
    fmtCurrency(this.minCanone()),
  );

  protected readonly diffFormatted = computed(() => {
    const min = this.minCanone();
    const proposed = this.proposedCanone();
    if (min == null || proposed == null) return '';
    return fmtCurrency(proposed - min);
  });

  protected readonly proposedCanoneCardClass = computed(() => {
    const min = this.minCanone();
    const proposed = this.proposedCanone();
    if (proposed == null) return 'border-slate-200 bg-slate-50';
    if (min != null && proposed < min) return 'border-red-200 bg-red-50';
    return 'border-green-200 bg-green-50';
  });

  protected readonly widgets = computed(() => {
    const c = this.criteria();
    if (!c) return [];
    return extractWidgets(c.form as Record<string, unknown>);
  });

  protected applyCanone(): void {
    const value = this.draftCanone();
    if (value == null || this.canoneDisabled() || !this.canoneChanged()) return;
    this.canoneApply.emit(value);
  }

  protected resetCanone(): void {
    this.draftCanone.set(this.proposedCanone());
  }

  protected getAnswer(widgetId: string): unknown {
    return this.pefState.economicOfferAnswer()?.[widgetId] ?? null;
  }

  protected setAnswer(widgetId: string, value: unknown): void {
    this.pefState.economicOfferAnswer.update((current) => ({
      ...(current ?? {}),
      [widgetId]: value,
    }));
  }
}
